<template>
  <div class="dynamic-renderer">
    <div v-if="displaySchema && displaySchema.elements" :class="containerClass">
      <component
        v-for="(element, index) in displaySchema.elements"
        :key="element.id || `${element.type}-${index}`"
        :is="() => renderElement(element, index)"
      />
    </div>
    <div v-else-if="!data" class="text-center py-8 text-gray-500">
      No data to display
    </div>
    <div v-else class="text-center py-8 text-gray-500">
      No display schema provided
    </div>
  </div>
</template>

<script>
import { h, resolveComponent } from 'vue';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';

// Tailwind text-color classes for section-header icons (mirrors the chart
// biopsy's BiopsySection color map).
const ICON_COLOR = {
  red: 'text-red-500',
  amber: 'text-amber-500',
  purple: 'text-purple-500',
  blue: 'text-blue-500',
  green: 'text-green-500',
  teal: 'text-teal-500',
  indigo: 'text-indigo-500',
  gray: 'text-gray-400',
};

export default {
  name: 'DynamicRenderer',
  components: { Chart_V5 },
  props: {
    data: {
      type: [Object, Array, null],
      required: true
    },
    displaySchema: {
      type: Object,
      required: false,
      default: null
    },
    showEntryHeaders: {
      type: Boolean,
      default: true
    },
    dense: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    // Layout is schema-driven:
    //  "list"    — dense label:value rows (space-y-1, tight padding).
    //  "compact" — the chart biopsy's gray-bg card stack (space-y-3).
    //  default   — roomy original.
    containerClass() {
      const layout = this.displaySchema && this.displaySchema.layout;
      if (layout === 'list') return 'space-y-1 p-3';
      if (layout === 'compact') return 'space-y-3 p-4 bg-gray-50';
      return this.dense ? 'space-y-3' : 'space-y-6 p-6';
    },
  },
  methods: {
    // Resolve data paths using dot notation
    resolveValue(pathOrValue, itemContext = null) {
      if (pathOrValue === null || pathOrValue === undefined) return '';

      // If not a string, return as-is (literal value)
      if (typeof pathOrValue !== 'string') return pathOrValue;

      // Handle item context for list templates
      if (itemContext && pathOrValue.includes('{item')) {
        return pathOrValue.replace(/\{item\.(\w+)\}/g, (match, key) => {
          return itemContext[key] !== undefined ? itemContext[key] : '';
        }).replace('{item}', String(itemContext));
      }

      // Check if it looks like a path. Segments may be identifiers OR array
      // indices, so "notes.0" and "episodes.2.facility" both resolve.
      if (/^[a-zA-Z_]\w*(\.(?:\w+))*$/.test(pathOrValue)) {
        // Try to resolve as a path in the data
        const value = this.getNestedValue(this.data, pathOrValue);
        // If we found a value at this path, return it; otherwise treat as literal
        if (value !== undefined) {
          return value;
        }
      }

      // Return as literal string
      return pathOrValue;
    },

    // Helper to get nested value from object using dot notation
    getNestedValue(obj, path) {
      if (!obj) return undefined;

      const parts = path.split('.');
      let current = obj;

      for (const part of parts) {
        if (current === null || current === undefined) return undefined;
        current = current[part];
      }

      return current;
    },

    // Auto-detect format based on value/key name
    autoDetectFormat(value, key = '') {
      if (typeof value === 'number') {
        const keyLower = key.toLowerCase();
        if (keyLower.includes('percent') || keyLower.includes('rate') || keyLower.includes('score')) {
          return 'percent';
        }
        if (keyLower.includes('price') || keyLower.includes('cost') || keyLower.includes('amount')) {
          return 'currency';
        }
      }
      return null;
    },

    // Format a value for display
    formatValue(value, format = null, key = '') {
      if (value === null || value === undefined || value === '') return '—';

      // Auto-detect format if not specified
      if (!format) {
        format = this.autoDetectFormat(value, key);
      }

      switch (format) {
        case 'percent':
          return typeof value === 'number' ? `${value}%` : value;
        case 'currency':
          return typeof value === 'number' ? `$${value.toLocaleString()}` : value;
        case 'number':
          return typeof value === 'number' ? value.toLocaleString() : value;
        default:
          return String(value);
      }
    },

    // Main render function
    renderElement(element, index = 0) {
      if (!element || !element.type) return null;

      switch (element.type) {
        case 'section':
          return this.renderSection(element);
        case 'card':
          return this.renderCard(element);
        case 'metric':
          return this.renderMetric(element);
        case 'text':
          return this.renderText(element);
        case 'list':
          return this.renderList(element);
        case 'key_value_list':
          return this.renderKeyValueList(element);
        case 'nested_list':
          return this.renderNestedList(element);
        case 'table':
          return this.renderTable(element);
        case 'alert':
          return this.renderAlert(element);
        case 'grid':
          return this.renderGrid(element);
        case 'chart':
          return this.renderChart(element);
        default:
          return null;
      }
    },

    // Render a section. When `icon`/`color` are supplied it renders the boxed,
    // colored-header card chrome (identical to the chart biopsy's BiopsySection);
    // otherwise it renders a plain titled block.
    renderSection(element) {
      // If any child text resolves to empty, drop the whole section (mirrors the
      // biopsy's v-if guards so empty fields don't render an empty card).
      const childEls = (element.children || [])
        .map((child, idx) => this.renderElement(child, idx))
        .filter(Boolean);
      if (element.children && element.children.length && childEls.length === 0) {
        return null;
      }

      // Boxed/colored-header variant (biopsy sections).
      if (element.icon || element.color) {
        const iconColor = ICON_COLOR[element.color] || ICON_COLOR.gray;
        const header = h('div', {
          class: 'flex items-center gap-2 px-3 py-2 border-b border-gray-100'
        }, [
          element.icon
            ? h(resolveComponent('FontAwesomeIcon'), {
                icon: ['fas', element.icon],
                class: `text-[10px] ${iconColor}`,
              })
            : null,
          h('span', {
            class: 'text-[10px] uppercase tracking-wider font-semibold text-gray-600'
          }, element.title || ''),
        ].filter(Boolean));

        const body = h('div', { class: 'px-3 py-2' }, childEls);

        return h('div', {
          class: 'bg-white border border-gray-200 rounded-lg overflow-hidden'
        }, [header, body]);
      }

      // Plain titled block (default).
      const children = [];
      if (element.title) {
        children.push(h('h3', {
          class: this.dense ? 'text-sm font-semibold text-gray-900 mb-1.5' : 'text-lg font-semibold text-gray-900 mb-4'
        }, element.title));
      }
      if (childEls.length > 0) {
        children.push(h('div', { class: this.dense ? 'space-y-2' : 'space-y-4' }, childEls));
      }
      return h('div', { class: this.dense ? '' : 'mb-6' }, children);
    },

    // Render a light "card" box with a small uppercase label + value (mirrors
    // the chart biopsy's Patient Snapshot box).
    renderCard(element) {
      const value = this.resolveValue(element.value);
      if (value === null || value === undefined || value === '') return null;

      const children = [];
      if (element.label) {
        children.push(h('div', {
          class: 'text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-1'
        }, element.label));
      }
      children.push(h('p', {
        class: 'text-xs text-gray-700 leading-relaxed'
      }, String(value)));

      return h('div', {
        class: 'bg-white rounded-lg p-3 border border-gray-100'
      }, children);
    },

    // Render a metric (key number)
    renderMetric(element) {
      const value = this.resolveValue(element.value);
      const formatted = this.formatValue(value, element.format, element.value);

      const children = [];

      if (element.label) {
        children.push(h('div', {
          class: this.dense ? 'text-xs text-gray-600' : 'text-sm text-gray-600 mb-1'
        }, element.label));
      }

      children.push(h('div', {
        class: this.dense ? 'text-base font-semibold text-gray-900' : 'text-2xl font-bold text-gray-900'
      }, formatted));

      return h('div', {
        class: this.dense ? 'inline-flex items-baseline gap-2 rounded-md bg-gray-50 px-3 py-1.5' : 'bg-gray-50 rounded-lg p-4'
      }, children);
    },

    // Render text. Empty resolves to null (so section guards can drop the parent).
    renderText(element) {
      const value = this.resolveValue(element.value);
      if (value === null || value === undefined || value === '') return null;

      if (element.label) {
        return h('div', { class: 'text-xs text-gray-700 leading-snug' }, [
          h('span', { class: 'font-semibold mr-1' }, `${element.label}:`),
          String(value),
        ]);
      }
      // No label — biopsy body paragraph style.
      return h('p', { class: this.dense ? 'text-xs text-gray-700 leading-snug' : 'text-xs text-gray-700 leading-relaxed' }, String(value));
    },

    // Render an OPEN array of {label, value} objects as tight "Label: value"
    // rows. For dynamic fact lists where the fields aren't known ahead of time.
    // element.items = the data path (e.g. "facts"); labelKey/valueKey optional.
    renderKeyValueList(element) {
      const items = this.resolveValue(element.items) || [];
      if (!Array.isArray(items) || items.length === 0) return null;
      const lk = element.labelKey || 'label';
      const vk = element.valueKey || 'value';
      const rows = items
        .filter((it) => it && (it[vk] !== undefined && it[vk] !== null && it[vk] !== ''))
        .map((it, idx) => h('div', { key: idx, class: 'text-xs text-gray-700 leading-snug' }, [
          h('span', { class: 'font-semibold mr-1' }, `${it[lk]}:`),
          String(it[vk]),
        ]));
      if (!rows.length) return null;
      return h('div', { class: 'space-y-1' }, rows);
    },

    // Render a list
    renderList(element) {
      const items = this.resolveValue(element.items) || [];

      if (!Array.isArray(items) || items.length === 0) {
        return h('div', { class: 'text-gray-500 text-sm' }, 'No items');
      }

      const listItems = items.map((item, idx) => {
        let content = item;
        if (element.itemTemplate) {
          content = this.resolveValue(element.itemTemplate, item);
        }

        return h('li', {
          key: idx,
          class: this.dense ? 'text-xs text-gray-700' : 'text-sm text-gray-700 mb-1'
        }, content);
      });

      return h('ul', { class: this.dense ? 'space-y-0.5 pl-4 list-disc' : 'space-y-1 pl-4 list-disc' }, listItems);
    },

    // Render a nested list (array of objects with defined fields)
    renderNestedList(element) {
      const items = this.resolveValue(element.items) || [];

      if (!Array.isArray(items) || items.length === 0) {
        return h('div', { class: 'text-gray-500 text-sm italic p-4' }, 'No entries recorded');
      }

      const children = [];

      // Add label/title if provided
      if (element.label) {
        children.push(h('h4', {
          class: this.dense ? 'text-sm font-semibold text-gray-900 mb-1.5' : 'text-sm font-semibold text-gray-900 mb-3'
        }, element.label));
      }

      // Render each item as a card
      const itemCards = items.map((item, idx) => {
        const cardChildren = [];

        // Add item number/header
        if (element.showEntryHeader !== false && this.showEntryHeaders) {
          // Prefer a meaningful title from the item itself; "Entry 3" tells the
          // reader nothing. titleKey wins, else the first title-ish field.
          const titleKey = element.titleKey
            || ['title', 'label', 'name', 'facility', 'drug', 'admit_date', 'date']
                 .find((k) => item && item[k]);
          const heading = titleKey && item[titleKey] != null && item[titleKey] !== ''
            ? String(item[titleKey])
            : `${element.label || 'Entry'} ${idx + 1}`;
          cardChildren.push(h('div', {
            class: this.dense ? 'text-xs font-semibold text-primary-600 mb-0.5' : 'text-xs font-semibold text-primary-600 mb-2'
          }, heading));
        }

        // Render each field in the item
        if (element.fields && Array.isArray(element.fields)) {
          const sortedFields = [...element.fields].sort((a, b) => (a.order || 0) - (b.order || 0));

          const fieldElements = sortedFields.map(field => {
            const value = this.getNestedValue(item, field.id);

            // Skip if no value
            if (value === undefined || value === null || value === '') {
              return null;
            }

            return this.renderNestedField(field, value);
          }).filter(Boolean);

          cardChildren.push(h('div', { class: this.dense ? 'flex flex-wrap gap-x-4 gap-y-0.5' : 'space-y-2' }, fieldElements));
        } else {
          // Fallback: no `fields` declared, so render the object's own keys.
          // Values may be arrays or nested objects — String() would emit
          // "a,b,c" or "[object Object]", so handle those shapes explicitly.
          const fieldElements = Object.entries(item).map(([key, value]) => {
            if (value === undefined || value === null || value === '') return null;
            if (Array.isArray(value) && value.length === 0) return null;

            const label = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

            // Array of scalars -> bulleted list. Array of objects -> one row each.
            if (Array.isArray(value)) {
              return h('div', { class: this.dense ? 'w-full text-xs' : 'text-sm' }, [
                h('div', { class: 'font-semibold text-gray-700 mb-0.5' }, label),
                h('ul', { class: 'list-disc pl-5 space-y-0.5' },
                  value.map((v, i) => h('li', { key: i, class: 'text-gray-600' },
                    typeof v === 'object' && v !== null
                      ? Object.entries(v)
                          .filter(([, vv]) => vv !== null && vv !== undefined && vv !== '')
                          .map(([kk, vv]) => `${kk.replace(/_/g, ' ')}: ${Array.isArray(vv) ? vv.join(', ') : vv}`)
                          .join(' · ')
                      : String(v)
                  ))),
              ]);
            }

            // Nested object -> inline "k: v · k: v" so it never prints [object Object].
            if (typeof value === 'object') {
              const inner = Object.entries(value)
                .filter(([, vv]) => vv !== null && vv !== undefined && vv !== '' &&
                                    !(Array.isArray(vv) && vv.length === 0))
                .map(([kk, vv]) => `${kk.replace(/_/g, ' ')}: ${Array.isArray(vv) ? vv.join(', ') : vv}`)
                .join(' · ');
              if (!inner) return null;
              return h('div', { class: this.dense ? 'text-xs' : 'text-sm' }, [
                h('span', { class: 'font-semibold text-gray-700 mr-2' }, `${label}:`),
                h('span', { class: 'text-gray-600' }, inner),
              ]);
            }

            return h('div', { class: this.dense ? 'text-xs' : 'text-sm' }, [
              h('span', { class: 'font-semibold text-gray-700 mr-2' }, `${label}:`),
              h('span', { class: 'text-gray-600' }, this.formatValue(value, null, key)),
            ]);
          }).filter(Boolean);

          cardChildren.push(h('div', { class: this.dense ? 'flex flex-wrap gap-x-4 gap-y-0.5' : 'space-y-2' }, fieldElements));
        }

        return h('div', {
          key: idx,
          class: this.dense ? 'bg-gray-50 border border-gray-200 rounded-md px-3 py-2' : 'bg-gray-50 border border-gray-200 rounded-lg p-4'
        }, cardChildren);
      });

      children.push(h('div', { class: this.dense ? 'space-y-1.5' : 'space-y-3' }, itemCards));

      return h('div', { class: this.dense ? '' : 'mb-4' }, children);
    },

    // Render a field within a nested list item
    renderNestedField(field, value) {
      let displayValue = value;
      // Format value based on field type and options
      switch (field.type) {
        case 'select':
        case 'radio':
          // Find the label for the selected option
          const selectedOption = field.options?.find(opt => opt.value == value);
          displayValue = selectedOption ? selectedOption.label : value;
          break;

        case 'checkbox':
          // Multiple selections
          if (Array.isArray(value)) {
            const labels = value.map(val => {
              const option = field.options?.find(opt => opt.value == val);
              return option ? option.label : val;
            });
            displayValue = labels.join(', ');
          }
          break;

        case 'boolean':
          displayValue = value ? 'Yes' : 'No';
          break;

        case 'textarea':
          // For textarea, show as a block
          return h('div', { class: this.dense ? 'w-full text-xs' : 'text-sm' }, [
            h('div', { class: this.dense ? 'font-semibold text-gray-700' : 'font-semibold text-gray-700 mb-1' }, field.question),
            h('div', { class: 'text-gray-600 whitespace-pre-wrap' }, String(value))
          ]);

        case 'date':
          if (value) {
            // Check if it's a date-only string (YYYY-MM-DD format)
            if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
              // Parse as local date by adding time component
              const [year, month, day] = value.split('-').map(Number);
              const date = new Date(year, month - 1, day); // month is 0-indexed
              displayValue = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              });
            } else {
              // Parse as regular timestamp
              const date = new Date(value);
              displayValue = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              });
            }
          }
          break;

        case 'number':
          displayValue = typeof value === 'number' ? value.toLocaleString() : value;
          break;

        default:
          displayValue = String(value);
      }

      // Standard field rendering
      return h('div', { class: this.dense ? 'text-xs' : 'text-sm' }, [
        h('span', { class: this.dense ? 'font-semibold text-gray-700 mr-1' : 'font-semibold text-gray-700 mr-2' }, `${field.question}:`),
        h('span', { class: 'text-gray-600' }, displayValue)
      ]);
    },

    // Render a table
    renderTable(element) {
      const data = this.resolveValue(element.data) || [];

      if (!Array.isArray(data) || data.length === 0) {
        return h('div', { class: 'text-gray-500 text-sm' }, 'No data');
      }

      // Use provided columns or auto-generate
      let columns = element.columns;

      if (!columns || columns.length === 0) {
        // Auto-generate columns from first data item
        const firstItem = data[0];
        columns = Object.keys(firstItem || {}).map(key => ({
          key: key,
          label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          format: this.autoDetectFormat(firstItem[key], key)
        }));
      }

      // Create header
      const headerCells = columns.map(col =>
        h('th', {
          class: 'px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase'
        }, col.label || col.key)
      );
      const header = h('thead', { class: 'bg-gray-50' },
        h('tr', {}, headerCells)
      );

      // Create rows
      const rows = data.map((row, idx) => {
        const cells = columns.map(col => {
          const value = row[col.key];
          const formatted = this.formatValue(value, col.format, col.key);

          return h('td', {
            class: 'px-4 py-2 text-sm text-gray-900 whitespace-pre-wrap'
          }, formatted);
        });

        return h('tr', {
          key: idx,
          class: idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
        }, cells);
      });

      const tbody = h('tbody', {
        class: 'divide-y divide-gray-200'
      }, rows);

      const table = h('table', {
        class: 'min-w-full divide-y divide-gray-200'
      }, [header, tbody]);

      return h('div', { class: 'overflow-x-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg' }, table);
    },

    // Render an alert
    renderAlert(element) {
      const alertStyles = {
        info: 'bg-blue-50 border-blue-200 text-blue-800',
        warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
        error: 'bg-red-50 border-red-200 text-red-800',
        success: 'bg-green-50 border-green-200 text-green-800'
      };

      const severity = element.severity || 'info';
      const message = this.resolveValue(element.message);

      return h('div', {
        class: `border rounded-lg p-4 ${alertStyles[severity]}`
      }, message);
    },

    /**
     * Render a chart via Chart_V5 (the standard chart component).
     *
     * The element is declarative and references data by path, exactly like
     * `table` does — it never carries values inline:
     *   {
     *     "type": "chart",
     *     "chartType": "vertical_bar" | "horizontal_bar" | "line" | "pie"
     *                  | "stacked_bar" | "grouped_bar",
     *     "data": "cost_by_category",   // path to an array of objects
     *     "labelKey": "category",       // field for the x/category axis
     *     "series": [                   // one entry per plotted measure
     *       { "key": "paid", "label": "Paid", "type": "bar", "color": "#2563EB" }
     *     ],
     *     "title": "...", "subtitle": "...", "height": "h-80",
     *     "format": "currency" | "number" | "percent",
     *     "horizontal": false           // shorthand alias for horizontal_bar
     *   }
     *
     * Density caps from the design system are enforced here, not left to the
     * schema author: bars/pie slices are truncated so a chart can never render
     * past the documented limits.
     */
    renderChart(element) {
      const rows = this.resolveValue(element.data);
      if (!Array.isArray(rows) || rows.length === 0) return null;

      const seriesDefs = Array.isArray(element.series) && element.series.length
        ? element.series
        : [{ key: element.valueKey || 'value', label: element.label || 'Value' }];

      let chartType = element.chartType || (element.horizontal ? 'horizontal_bar' : 'vertical_bar');

      // Density limits (app_ui/data_visualization): <=12 bars, <=6 lines,
      // <=6 pie segments. Truncate rather than render an unreadable chart.
      const isPie = chartType === 'pie';
      const isLine = chartType === 'line';
      const cap = isPie ? 6 : (isLine ? 60 : 12);
      const capped = rows.length > cap ? rows.slice(0, cap) : rows;

      const labelKey = element.labelKey || 'label';
      const labels = capped.map((r) => String(r[labelKey] ?? ''));

      // Default palette: Tailwind 600-level, max 8 (per the design system).
      const PALETTE = ['#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED', '#EA580C', '#0284C7', '#DB2777'];

      const series = seriesDefs.slice(0, 8).map((s, i) => ({
        values: capped.map((r) => {
          const v = r[s.key];
          const n = typeof v === 'number' ? v : parseFloat(v);
          return Number.isFinite(n) ? n : 0;
        }),
        config: {
          label: s.label || s.key,
          backgroundColor: s.color || PALETTE[i % PALETTE.length],
          type: s.type || (isLine ? 'line' : 'bar'),
          yAxis: s.yAxis || 'left',
          offset: chartType === 'grouped_bar' ? i : 0,
        },
      }));

      // Currency/number/percent tick formatting, mirroring formatValue().
      const fmt = element.format || this.autoDetectFormat(
        capped[0] ? capped[0][seriesDefs[0].key] : null, seriesDefs[0].key
      );
      const tickFormat = fmt === 'currency'
        ? (v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
        : fmt === 'percent'
          ? (v) => `${v}%`
          : (v) => Number(v).toLocaleString();

      const chart_data = { series, labels };
      const chart_config = {
        chartType,
        margin: { top: element.title ? 60 : 20, right: 40, bottom: 70, left: 90 },
        maxBarWidth: 40,
        xAxis: { labelRotation: -45, fontSize: 11 },
        yAxes: [
          { position: 'left', tickFormat, fontSize: 11, maxHorizontalLabelLength: 40 },
          { position: 'right', fontSize: 11 },
        ],
        legend: { display: series.length > 1, position: 'top', spacing: 12 },
        formatting: { percentage: false, sig_figs: 0 },
        title: element.title || '',
        subtitle: element.subtitle
          || (rows.length > cap ? `Top ${cap} of ${rows.length}` : ''),
      };

      return h('div', { class: 'bg-white rounded-lg border border-gray-100 p-2' }, [
        h(resolveComponent('Chart_V5'), {
          chart_data,
          chart_config,
          overrideHeight: element.height || 'h-80',
        }),
      ]);
    },

    // Render a grid layout
    renderGrid(element) {
      const cols = element.columns || 2;
      const gridClass = `grid grid-cols-1 md:grid-cols-${cols} gap-4`;

      const children = element.children?.map((child, idx) =>
        this.renderElement(child, idx)
      ) || [];

      return h('div', { class: gridClass }, children);
    }
  }
};
</script>

<style scoped>
@reference "../../../assets/theme.css";
.dynamic-renderer {
  @apply w-full;
}
</style>
